import {
	ChevronLeft,
	ChevronRight,
	Filter,
	Hash,
	Link2,
	Loader2,
	Plus,
	Search,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import type {
	CategoryMapping,
	PaginatedResponse,
} from "../../types/categories";
import { Chip } from "../chip";
import { AddKeywordModal } from "./AddKeywordModal";

type CatOption = { id: number; name: string; fullPath: string };

export const MappingsTab = () => {
	const [search, setSearch] = useState("");
	const [typeFilter, setTypeFilter] = useState<"all" | "keyword" | "hierarchy">(
		"all",
	);
	const [page, setPage] = useState(1);
	const [data, setData] = useState<PaginatedResponse<CategoryMapping> | null>(
		null,
	);
	const [loading, setLoading] = useState(true);

	// Category filter
	const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
		null,
	);
	const [selectedCategoryName, setSelectedCategoryName] = useState("");
	const [showCategoryPicker, setShowCategoryPicker] = useState(false);
	const [categorySearch, setCategorySearch] = useState("");
	const [categoryOptions, setCategoryOptions] = useState<CatOption[]>([]);
	const [catSearchLoading, setCatSearchLoading] = useState(false);
	const catDebounce = useRef<ReturnType<typeof setTimeout>>(undefined);
	const [showAddKeyword, setShowAddKeyword] = useState(false);

	// Debounced search for category picker
	useEffect(() => {
		clearTimeout(catDebounce.current);
		if (!showCategoryPicker) return;
		catDebounce.current = setTimeout(async () => {
			setCatSearchLoading(true);
			try {
				const q = categorySearch.trim();
				const { data } = q
					? await api.categories.search.get({ query: { q } })
					: await api.categories.children.get();
				const rows = (data ?? []) as unknown as CatOption[];
				setCategoryOptions(rows.slice(0, 30));
			} catch {
				setCategoryOptions([]);
			} finally {
				setCatSearchLoading(false);
			}
		}, 250);
	}, [categorySearch, showCategoryPicker]);

	// Fetch mappings from DB
	const fetchMappings = useCallback(async () => {
		setLoading(true);
		try {
			const query: Record<string, string> = { page: String(page) };
			if (search.trim()) query.q = search.trim();
			if (typeFilter !== "all") query.type = typeFilter;
			if (selectedCategoryId) query.categoryId = String(selectedCategoryId);
			const { data } = await api["category-mappings"].get({ query });
			if (data) setData(data as unknown as PaginatedResponse<CategoryMapping>);
		} catch {
			setData(null);
		} finally {
			setLoading(false);
		}
	}, [page, search, typeFilter, selectedCategoryId]);

	// Debounced fetch on filter changes
	const fetchDebounce = useRef<ReturnType<typeof setTimeout>>(undefined);
	useEffect(() => {
		clearTimeout(fetchDebounce.current);
		fetchDebounce.current = setTimeout(() => {
			fetchMappings();
		}, 300);
		return () => clearTimeout(fetchDebounce.current);
	}, [fetchMappings]);

	// Reset page when filters change
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset page on filter change
	useEffect(() => {
		setPage(1);
	}, [search, typeFilter, selectedCategoryId]);

	const mappings = data?.items ?? [];

	// Group mappings by categoryId
	type GroupedCategory = {
		categoryId: number;
		categoryName: string;
		mappings: CategoryMapping[];
	};

	const grouped = useMemo(() => {
		const map = new Map<number, GroupedCategory>();
		for (const m of mappings) {
			if (!map.has(m.categoryId)) {
				map.set(m.categoryId, {
					categoryId: m.categoryId,
					categoryName: m.categoryName ?? "Unknown category",
					mappings: [],
				});
			}
			const group = map.get(m.categoryId);
			if (group) group.mappings.push(m);
		}
		return Array.from(map.values());
	}, [mappings]);

	return (
		<div className="space-y-5">
			{/* Filters bar */}
			<div className="bg-white rounded-xl border border-slate-200/60 p-4">
				<div className="flex flex-wrap items-center gap-3">
					{/* Search */}
					<div className="relative flex-1 min-w-50">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
						<input
							type="text"
							placeholder="Search keywords or paths..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 focus:bg-white transition-all"
						/>
					</div>

					{/* Category filter */}
					<div className="relative">
						<button
							type="button"
							onClick={() => setShowCategoryPicker(!showCategoryPicker)}
							className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
								selectedCategoryId
									? "bg-primary/5 border-primary/20 text-primary"
									: "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
							}`}
						>
							<Filter className="w-4 h-4" />
							{selectedCategoryId ? (
								<>
									<span className="max-w-45 truncate">
										{selectedCategoryName}
									</span>
									<span className="text-[10px] font-mono opacity-60">
										#{selectedCategoryId}
									</span>
								</>
							) : (
								"Filter by Category"
							)}
						</button>

						{/* Category picker dropdown */}
						{showCategoryPicker && (
							<div className="absolute top-full left-0 mt-2 w-96 bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden">
								<div className="p-3 border-b border-slate-100">
									<div className="relative">
										<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
										<input
											type="text"
											placeholder="Search category by name or ID..."
											value={categorySearch}
											onChange={(e) => setCategorySearch(e.target.value)}
											className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
										/>
									</div>
								</div>
								<div className="max-h-64 overflow-y-auto p-1">
									{catSearchLoading ? (
										<div className="flex items-center justify-center py-6">
											<Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
										</div>
									) : categoryOptions.length === 0 ? (
										<div className="text-center py-6 text-sm text-slate-400">
											No categories found
										</div>
									) : (
										categoryOptions.map((cat) => (
											<button
												key={cat.id}
												type="button"
												onClick={() => {
													setSelectedCategoryId(cat.id);
													setSelectedCategoryName(cat.name);
													setShowCategoryPicker(false);
													setCategorySearch("");
												}}
												className={`w-full flex flex-col gap-0.5 px-3 py-2.5 rounded-lg text-left transition-colors ${
													selectedCategoryId === cat.id
														? "bg-primary/10"
														: "hover:bg-slate-50"
												}`}
											>
												<span className="text-sm font-medium text-slate-700 truncate">
													{cat.name}
													<span className="ml-2 text-[10px] font-mono text-slate-400">
														#{cat.id}
													</span>
												</span>
												{cat.fullPath && (
													<span className="text-[11px] text-slate-400 truncate">
														{cat.fullPath}
													</span>
												)}
											</button>
										))
									)}
								</div>
								{selectedCategoryId && (
									<div className="p-2 border-t border-slate-100">
										<button
											type="button"
											onClick={() => {
												setSelectedCategoryId(null);
												setSelectedCategoryName("");
												setShowCategoryPicker(false);
											}}
											className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm text-danger hover:bg-danger/5 transition-colors"
										>
											<X className="w-3.5 h-3.5" />
											Clear filter
										</button>
									</div>
								)}
							</div>
						)}
					</div>

					{/* Type filter */}
					<div className="flex items-center rounded-xl border border-slate-200 overflow-hidden">
						{(["all", "keyword", "hierarchy"] as const).map((t) => (
							<button
								key={t}
								type="button"
								onClick={() => setTypeFilter(t)}
								className={`px-3.5 py-2.5 text-xs font-semibold capitalize transition-colors ${
									typeFilter === t
										? "bg-primary text-white"
										: "bg-white text-slate-500 hover:bg-slate-50"
								}`}
							>
								{t}
							</button>
						))}
					</div>

					{/* Add keyword CTA */}
					<button
						type="button"
						onClick={() => setShowAddKeyword(true)}
						className="ml-auto flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand text-black text-xs font-semibold hover:bg-brand-hover transition-colors"
					>
						<Plus className="w-3.5 h-3.5" />
						Add Keyword
					</button>
				</div>

				{/* Active filter tags */}
				{selectedCategoryId && (
					<div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
						<span className="text-xs text-slate-400">Filtering:</span>
						<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
							<Hash className="w-3 h-3" />
							{selectedCategoryId} — {selectedCategoryName}
							<button
								type="button"
								onClick={() => {
									setSelectedCategoryId(null);
									setSelectedCategoryName("");
								}}
								className="ml-0.5 hover:bg-primary/20 rounded-full p-0.5 transition-colors"
							>
								<X className="w-3 h-3" />
							</button>
						</span>
					</div>
				)}
			</div>

			{/* Results count + pagination info */}
			<div className="flex items-center justify-between px-1">
				<p className="text-sm text-slate-500">
					{loading ? (
						"Loading…"
					) : (
						<>
							<span className="font-semibold text-slate-700">
								{data?.total ?? 0}
							</span>{" "}
							mapping{data?.total !== 1 ? "s" : ""} found
						</>
					)}
				</p>
				{data && data.totalPages > 1 && (
					<p className="text-xs text-slate-400">
						Page {data.page} of {data.totalPages}
					</p>
				)}
			</div>

			{/* Mappings list — grouped by category */}
			{loading ? (
				<div className="bg-white rounded-xl border border-slate-200/60 p-12 flex items-center justify-center">
					<Loader2 className="w-6 h-6 text-primary animate-spin" />
				</div>
			) : grouped.length === 0 ? (
				<div className="bg-white rounded-xl border border-slate-200/60 p-12 text-center">
					<div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
						<Link2 className="w-7 h-7 text-slate-300" />
					</div>
					<h3 className="text-lg font-semibold text-slate-700">
						No mappings found
					</h3>
					<p className="text-sm text-slate-400 mt-1 max-w-sm mx-auto">
						{selectedCategoryId
							? `No keyword mappings exist for "${selectedCategoryName}" yet.`
							: "Try adjusting your search or filters."}
					</p>
				</div>
			) : (
				<div className="space-y-4">
					{grouped.map((group) => {
						const keywords = group.mappings.filter((m) => m.type === "keyword");
						const hierarchies = group.mappings.filter(
							(m) => m.type === "hierarchy",
						);
						return (
							<div
								key={group.categoryId}
								className="bg-white rounded-xl border border-slate-200/60 overflow-hidden hover:shadow-md hover:shadow-slate-100 transition-all duration-200"
							>
								{/* Category header */}
								<div className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 bg-slate-50/40">
									<span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md bg-primary/10 text-primary">
										#{group.categoryId}
									</span>
									<span className="text-sm font-semibold text-slate-700">
										{group.categoryName}
									</span>
									<span className="ml-auto text-[11px] text-slate-400">
										{group.mappings.length} mapping
										{group.mappings.length !== 1 ? "s" : ""}
									</span>
								</div>

								{/* Mapping chips */}
								<div className="px-5 py-4 space-y-3">
									{/* Keywords */}
									{keywords.length > 0 && (
										<div className="flex items-start gap-2">
											<span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider pt-1 shrink-0 w-20">
												Keywords
											</span>
											<div className="flex flex-wrap gap-1.5">
												{keywords.map((m) => (
													<Chip key={m.id} variant="info" size="sm">
														{m.keyword}
													</Chip>
												))}
											</div>
										</div>
									)}

									{/* Hierarchical paths */}
									{hierarchies.length > 0 && (
										<div className="flex items-start gap-2">
											<span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider pt-1 shrink-0 w-20">
												Paths
											</span>
											<div className="space-y-1.5">
												{hierarchies.map((m) => (
													<div
														key={m.id}
														className="flex items-center gap-1 flex-wrap"
													>
														{(m.hierarchicalPath ?? "")
															.split(/\s*(?:>|\|\||\|)\s*/)
															.map((part, i) => (
																<span
																	key={`${m.id}-${part}`}
																	className="flex items-center gap-1"
																>
																	{i > 0 && (
																		<ChevronRight className="w-3 h-3 text-slate-300" />
																	)}
																	<Chip variant="warning" size="xs">
																		{part}
																	</Chip>
																</span>
															))}
													</div>
												))}
											</div>
										</div>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}

			{/* Pagination */}
			{data && data.totalPages > 1 && (
				<div className="flex items-center justify-center gap-2 pt-2">
					<button
						type="button"
						disabled={page <= 1}
						onClick={() => setPage((p) => p - 1)}
						className="flex items-center gap-1 px-3.5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
					>
						<ChevronLeft className="w-4 h-4" />
						Previous
					</button>
					<div className="flex items-center gap-1">
						{Array.from({ length: Math.min(data.totalPages, 7) }, (_, i) => {
							let pageNum: number;
							if (data.totalPages <= 7) {
								pageNum = i + 1;
							} else if (page <= 4) {
								pageNum = i + 1;
							} else if (page >= data.totalPages - 3) {
								pageNum = data.totalPages - 6 + i;
							} else {
								pageNum = page - 3 + i;
							}
							return (
								<button
									key={pageNum}
									type="button"
									onClick={() => setPage(pageNum)}
									className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
										pageNum === page
											? "bg-primary text-white"
											: "text-slate-500 hover:bg-slate-100"
									}`}
								>
									{pageNum}
								</button>
							);
						})}
					</div>
					<button
						type="button"
						disabled={page >= data.totalPages}
						onClick={() => setPage((p) => p + 1)}
						className="flex items-center gap-1 px-3.5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
					>
						Next
						<ChevronRight className="w-4 h-4" />
					</button>
				</div>
			)}

			{/* Add keyword modal */}
			<AddKeywordModal
				open={showAddKeyword}
				onClose={() => setShowAddKeyword(false)}
				onCreated={() => fetchMappings()}
			/>
		</div>
	);
};
